import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { TooltipElement } from "@front-shared/elements/tooltip.element.tsx"
import { HtmlHelper as NirvanaHtmlHelper } from "@front-shared/helpers/html.helper"
import type { AvailableTextTypography } from "@front-shared/types/elements.types.ts"

import { CoverageInputDataDifferenceComponent } from "../components/coverageChecker/result/coverageInputDataDifference.component"
import type { ICoverageBreakdownData } from "../types/data.types.ts"

export class HtmlHelper extends NirvanaHtmlHelper {
    /**
     * Get HTML wrapped badge
     */
    static getHtmlWrappedBadge(
        label: string | JSX.Element,
        value?: string | number | JSX.Element,
        tooltip?: string,
        className = "",
        customBgClassName = "bg-brand-offWhite border-brand-black-transparent-010",
        labelTypography?: AvailableTextTypography,
        valueTypography?: AvailableTextTypography,
        wrapText = false,
    ): JSX.Element {
        return (
            <div
                className={`
                    flex items-center
                    px-[8px] pt-[3px] rounded-[4px]
                    border border-solid text-center
                    ${className}
                    ${customBgClassName}
                    ${!wrapText ? "whitespace-nowrap" : ""}
                `}
            >
                {typeof label === "string" ? (
                    <PrimaryText typography={labelTypography || "textSemibold"}>
                        {label}
                    </PrimaryText>
                ) : (
                    label
                )}

                {value ? (
                    <div className="ml-[8px]">
                        {typeof value === "string" ? (
                            <PrimaryText typography={valueTypography || "text"}>
                                {value}
                            </PrimaryText>
                        ) : (
                            value
                        )}
                    </div>
                ) : null}

                {tooltip && (
                    <TooltipElement
                        className="ml-[4px]"
                        isInlined
                        text={tooltip}
                    />
                )}
            </div>
        )
    }

    /**
     * Get formatted single coverage breakdown item
     */
    static getCoverageBreakdownItem(
        data: ICoverageBreakdownData,
        key?: number | string,
        className?: string,
        shorterLabelWidth?: boolean,
    ): JSX.Element {
        return (
            <div
                key={key}
                className={`
                    mt-[6px] flex items-start
                    ${className}
                `}
            >
                {data.label ? (
                    <div
                        className={`
                            mr-[16px] flex-shrink-0
                            ${shorterLabelWidth ? "min-w-[174px]" : "min-w-[190px]"}
                        `}
                    >
                        <PrimaryText typography="textSemibold">
                            {data.label}
                        </PrimaryText>
                    </div>
                ) : null}

                {data.valueDifference && typeof data.value === "string" ? (
                    <CoverageInputDataDifferenceComponent
                        className="mouseflow-ignore"
                        initialData={data.value || undefined}
                        overridenData={data.valueDifference}
                    />
                ) : (
                    <div
                        className={`
                            mouseflow-ignore relative
                            ${
                                data.isHighlighted &&
                                typeof data.value === "string"
                                    ? `
                                        bg-brand-greenHighlight px-[8px]
                                        border-b-[2px] border-solid border-brand-deepPurple
                                    `
                                    : ""
                            }
                        `}
                    >
                        {typeof data.value === "string" ? (
                            <PrimaryText
                                typography="text"
                                className={`
                                    ${
                                        data.isHighlighted
                                            ? "relative top-[2px]"
                                            : ""
                                    }

                                    ${
                                        data.flaggedField
                                            ? `pt-[4px] relative left-[-8px] bg-brand-white px-[8px] rounded-[4px]`
                                            : ""
                                    }
                                `}
                            >
                                {data.value}

                                {data.flaggedField && (
                                    <TooltipElement
                                        className="ml-[4px] relative top-[2px]"
                                        isInlined
                                        text={data.flaggedField.tooltip}
                                    />
                                )}
                            </PrimaryText>
                        ) : (
                            data.value
                        )}
                    </div>
                )}

                {/*INDICATOR for override in case of data being not string, for example badge*/}
                {data.isHighlighted && typeof data.value !== "string" ? (
                    <div className="relative top-[4px] ml-[4px] w-[14px] h-[14px] rounded-full bg-brand-greenHighlight" />
                ) : null}

                {data.tooltip && (
                    <TooltipElement
                        className={`
                            ml-[4px] relative
                            ${data.isHighlighted ? "top-[4px]" : "top-[2px]"}
                        `}
                        isInlined
                        text={data.tooltip}
                    />
                )}
            </div>
        )
    }

    /**
     * Get formatted single coverage breakdown item
     */
    static getCoverageBreakdownItemPlanYearReset(
        data: ICoverageBreakdownData,
        key?: number | string,
    ): JSX.Element {
        const isHighlighted =
            data.valueDifference && data.value !== data.valueDifference

        return (
            <div key={key} className="mt-[2px] flex items-center">
                <PrimaryText
                    className="w-[225px] mr-[8px]"
                    typography="textSemibold"
                >
                    {data.label}
                </PrimaryText>

                <div className="flex-1 mr-[8px]">
                    <PrimaryText
                        className={`inline-block w-auto pt-[2px]
                            ${isHighlighted ? "bg-brand-paleRed010 px-[4px]" : ""}
                        `}
                        typography="text"
                    >
                        {data.value}
                    </PrimaryText>
                </div>

                <div className="flex-1">
                    <PrimaryText
                        className={`inline-block w-auto pt-[2px] ${
                            isHighlighted
                                ? "bg-brand-greenHighlight px-[4px] border-b-[2px] border-solid border-brand-primary"
                                : ""
                        }`}
                        typography="text"
                    >
                        {data.valueDifference}
                    </PrimaryText>
                </div>
            </div>
        )
    }
}
